import React from 'react';
import { Select, Tooltip } from 'antd';
import { sparkToast, useReadOnly } from '@spark';
import { QuestionCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { data_from_server_post } from '@/shared/http/client';
import { useManualModelPredictions } from '@/features/model-setup/hooks/useManualModelPredictions';
import { useAuth } from '@/features/auth/AuthContext';

const ExternalModelResultsSelect = ({
    currentUsecase,
    label = <div className="fs-unmask">External Model Results</div>,
    helpText = <div className="fs-unmask">Select external model predictions to compare against benchmark data</div>,
    showOptional = true,
    onUpdateSuccess,
    isValidating = false,
    validationResult = null,
}) => {
    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    const {
        manualPredictionOptions,
        selectedManualPredictionDatasets,
        setSelectedManualPredictionDatasets,
    } = useManualModelPredictions(currentUsecase);

    // Convert array to single value (take first item if exists)
    const selectedValue = Array.isArray(selectedManualPredictionDatasets) && selectedManualPredictionDatasets.length > 0
        ? selectedManualPredictionDatasets[0]
        : undefined;

    const updateComparisonSettingsAfterSelection = () => {
        if (!currentUsecase) return;

        const clientId = currentUsecase.client_id;
        const usecaseId = currentUsecase._id;
        const currentOption = currentUsecase.comparison_mode_option || 'none';

        // When selecting external model results:
        // - Ensure comparison_mode is true
        // - If current option is "teacher" -> switch to "all"
        // - Otherwise -> "manual_model_predictions"
        const nextOption =
            currentOption === 'teacher' ? 'all' : 'manual_model_predictions';

        // First set comparison_mode = true
        data_from_server_post(
            'update_comparison_mode',
            {
                client_id: clientId,
                llm_usecase_id: usecaseId,
                comparison_mode: true,
            },
            () => {
                // Then update comparison_mode_option
                data_from_server_post(
                    'update_comparison_mode_option',
                    {
                        client_id: clientId,
                        llm_usecase_id: usecaseId,
                        comparison_mode_option: nextOption,
                    },
                    () => {
                        if (onUpdateSuccess) {
                            onUpdateSuccess();
                        }
                    },
                    (err) => {
                        console.warn(
                            'Failed to update comparison mode option from ExternalModelResultsSelect:',
                            err
                        );
                        sparkToast.error('Failed to update comparison mode option');
                    }
                );
            },
            (err) => {
                console.warn(
                    'Failed to update comparison mode from ExternalModelResultsSelect:',
                    err
                );
                sparkToast.error('Failed to enable comparison mode');
            }
        );
    };

    const updateComparisonSettingsAfterClear = () => {
        if (!currentUsecase) return;

        const clientId = currentUsecase.client_id;
        const usecaseId = currentUsecase._id;
        const currentOption = currentUsecase.comparison_mode_option || 'none';

        // When clearing external model results:
        // - If current is "all" -> change to "teacher" (keep commercial model checked)
        // - If current is "manual_model_predictions" -> change to "none" (uncheck external)
        // - If current is "teacher" -> stay "teacher" (commercial already checked, external already unchecked)
        // - Otherwise -> stay "none"
        let nextOption;
        if (currentOption === 'all') {
            nextOption = 'teacher';
        } else if (currentOption === 'manual_model_predictions') {
            nextOption = 'none';
        } else {
            // Already "teacher" or "none", no change needed
            nextOption = currentOption;
        }

        // Only update if there's an actual change
        if (nextOption === currentOption) {
            if (onUpdateSuccess) {
                onUpdateSuccess();
            }
            return;
        }

        data_from_server_post(
            'update_comparison_mode_option',
            {
                client_id: clientId,
                llm_usecase_id: usecaseId,
                comparison_mode_option: nextOption,
            },
            () => {
                if (onUpdateSuccess) {
                    onUpdateSuccess();
                }
            },
            (err) => {
                console.warn(
                    'Failed to update comparison mode option after clearing ExternalModelResultsSelect:',
                    err
                );
                sparkToast.error('Failed to update comparison mode option');
            }
        );
    };

    const handleChange = (value) => {
        // Convert single value to array for API (or empty array if cleared)
        const next = value ? [value] : [];
        setSelectedManualPredictionDatasets(next);

        if (currentUsecase) {
            data_from_server_post(
                "set_manual_model_predictions",
                {
                    client_id: currentUsecase.client_id,
                    llm_usecase_id: currentUsecase._id,
                    manual_model_predictions: next,
                },
                () => {
                    if (value) {
                        // After selecting an external model result, ensure comparison mode is enabled
                        // and comparison_mode_option follows the selection logic.
                        updateComparisonSettingsAfterSelection();
                    } else {
                        // After clearing the selection, adjust comparison_mode_option accordingly.
                        updateComparisonSettingsAfterClear();
                    }
                },
                (err) => {
                    console.warn("Failed to update manual model predictions:", err);
                    sparkToast.error("Failed to update external model results selection");
                }
            );
        }
    };

    // Helper function to get validation status message
    const getValidationMessage = () => {
        if (!validationResult || !validationResult.results) return null;

        const results = validationResult.results;
        const benchmarkNames = Object.keys(results);
        
        if (benchmarkNames.length === 0) return null;

        // Check overall status
        const allFullMatch = benchmarkNames.every(
            name => results[name].match_status === 'full_match'
        );
        const allNoMatch = benchmarkNames.every(
            name => results[name].match_status === 'no_match'
        );

        if (allFullMatch) {
            // All benchmarks fully match - show success message
            const totalMatches = benchmarkNames.reduce(
                (sum, name) => sum + (results[name].matches || 0),
                0
            );
            return {
                type: 'success',
                text: `All ${totalMatches} benchmark examples match with external model results`
            };
        }

        if (allNoMatch) {
            return {
                type: 'error',
                text: 'Benchmark examples do not match'
            };
        }

        // Partial match - show details
        const details = benchmarkNames.map(name => {
            const result = results[name];
            if (result.match_status === 'partial_match') {
                return `${result.matches}/${result.benchmark_total} examples match`;
            }
            return null;
        }).filter(Boolean);

        if (details.length > 0) {
            return {
                type: 'warning',
                text: `Benchmarks examples partially match (${details.join(', ')})`
            };
        }

        return null;
    };

    const validationMessage = getValidationMessage();

    return (
        <div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                gap: '4px'
            }}>
                <span style={{
                    fontWeight: 400,
                    color: '#222',
                    fontSize: '14px'
                }}>
                    {label}
                </span>
                {showOptional && (
                    <span style={{
                        color: '#8c8c8c',
                        fontSize: '14px',
                        fontWeight: 400
                    }}>
                        (optional)
                    </span>
                )}
                {helpText && (
                    <Tooltip title={helpText}>
                        <QuestionCircleOutlined style={{
                            color: '#878787',
                            fontSize: '12px',
                            cursor: 'help',
                            marginLeft: '4px'
                        }} />
                    </Tooltip>
                )}
            </div>
            <Select
                showSearch
                allowClear
                placeholder="Select external model results"
                // Rendered inside the Spark modal portal (z-index 1100);
                // lift the dropdown above it (antd default is 1050).
                dropdownStyle={{ zIndex: 1200 }}
                style={{
                    width: '100%',
                    borderRadius: '8px',
                    fontSize: '14px'
                }}
                value={selectedValue}
                onChange={handleChange}
                options={(manualPredictionOptions || []).map((m) => ({
                    value: m.id,
                    label: m.display_name || m.id,
                }))}
                disabled={readOnly || role === 'Viewer'}
                size="large"
                filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                suffixIcon={isValidating ? (
                    <Tooltip title="Validating benchmark matches with external model results">
                        <LoadingOutlined />
                    </Tooltip>
                ) : undefined}
                status={validationMessage?.type === 'error' ? 'error' : validationMessage?.type === 'warning' ? 'warning' : undefined}
            />
            {validationMessage && (
                <div style={{
                    marginTop: '8px',
                    color: validationMessage.type === 'error' 
                        ? '#ff4d4f' 
                        : validationMessage.type === 'warning' 
                        ? '#faad14' 
                        : '#52c41a',
                    fontSize: '14px',
                }}>
                    {validationMessage.text}
                </div>
            )}
        </div>
    );
};

export default ExternalModelResultsSelect;

